import React from "react";
import {Box} from "@mui/material";
import {alpha, useTheme} from "@mui/material/styles";

const LineTruckSvg = ({theme}) => (
    <Box component="svg" className="truck-svg" width="116" height="60" viewBox="0 0 116 60" role="img" aria-label="Truck on the way">
        <g className="truck-body" fill={alpha(theme.palette.primary.main, 0.05)} stroke={theme.palette.primary.main} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="6" y="9" width="56" height="31" rx="6" />
            <path d="M62 17 H79 C81.1 17 83.1 18.1 84.3 19.9 L91.4 30.9 C92.4 32.5 93 34.4 93 36.3 V37 C93 38.7 91.7 40 90 40 H62" />
            <path d="M67.5 23 H77 L82 30.5" fill="none" strokeWidth="2.25" />
        </g>
        <g className="truck-wheel">
            <circle cx="26" cy="46" r="7.5" fill={theme.palette.background.paper} stroke={theme.palette.primary.main} strokeWidth="2.5" />
            <path d="M26 41.5 V50.5" stroke={theme.palette.primary.main} strokeWidth="2" strokeLinecap="round" />
        </g>
        <g className="truck-wheel">
            <circle cx="78" cy="46" r="7.5" fill={theme.palette.background.paper} stroke={theme.palette.primary.main} strokeWidth="2.5" />
            <path d="M78 41.5 V50.5" stroke={theme.palette.primary.main} strokeWidth="2" strokeLinecap="round" />
        </g>
    </Box>
);

const SolidTruckSvg = ({theme}) => (
    <Box component="svg" className="truck-svg" width="112" height="62" viewBox="0 0 112 62" role="img" aria-label="Truck on the way">
        <g className="truck-body">
            <rect x="4" y="6" width="60" height="36" rx="5" fill={theme.palette.primary.main} />
            <rect x="4" y="6" width="60" height="7" rx="3.5" fill={alpha(theme.palette.background.paper, 0.18)} />
            <line x1="24" y1="12" x2="24" y2="38" stroke={alpha(theme.palette.background.paper, 0.35)} strokeWidth="1.5" />
            <line x1="44" y1="12" x2="44" y2="38" stroke={alpha(theme.palette.background.paper, 0.35)} strokeWidth="1.5" />
            <path d="M68 16 H82 C84.8 16 87.3 17.4 88.8 19.8 L95.6 30.6 C96.5 32.1 97 33.8 97 35.6 V39 C97 40.7 95.7 42 94 42 H68 Z" fill={theme.palette.primary.dark} />
            <path d="M72 20 H81 C82.4 20 83.7 20.7 84.5 21.9 L89 29 H72 Z" fill={alpha(theme.palette.background.paper, 0.92)} />
            <rect x="94.5" y="34" width="3.5" height="5.5" rx="1.75" fill={theme.palette.secondary.main} />
            <rect x="4" y="41" width="94" height="3.5" rx="1.75" fill={theme.palette.neutral[800]} />
        </g>
        {[24, 80].map((x) => (
            <g className="truck-wheel" key={x}>
                <circle cx={x} cy="48" r="8" fill={theme.palette.neutral[800]} />
                <circle cx={x} cy="48" r="4.5" fill={theme.palette.neutral[100]} />
                <path d={`M${x} 44.5 V51.5 M${x - 3.5} 48 H${x + 3.5}`} stroke={theme.palette.neutral[800]} strokeWidth="1.6" strokeLinecap="round" />
            </g>
        ))}
    </Box>
);

export const TrackingTruckIllustration = ({variant = "line", animated = false, muted = false}) => {
    const theme = useTheme();
    const isSolid = variant === "solid";

    if (!animated) {
        return (
            <Box sx={{width: isSolid ? 112 : 116, height: isSolid ? 62 : 60, mx: "auto", opacity: muted ? 0.35 : 1}}>
                {isSolid ? <SolidTruckSvg theme={theme} /> : <LineTruckSvg theme={theme} />}
            </Box>
        );
    }

    return (
        <Box
            sx={{
                position: "relative",
                width: 216,
                height: 86,
                mx: "auto",
                overflow: "hidden",
                "@keyframes truckBob": {
                    "0%, 100%": {transform: "translateY(0)"},
                    "50%": {transform: `translateY(${isSolid ? -1.5 : -1}px)`},
                },
                "@keyframes truckWheelSpin": {
                    from: {transform: "rotate(0deg)"},
                    to: {transform: "rotate(360deg)"},
                },
                "@keyframes truckRoadMove": {
                    from: {backgroundPosition: "0 0"},
                    to: {backgroundPosition: "-24px 0"},
                },
                "@keyframes truckSpeedLine": {
                    "0%": {opacity: 0, transform: "translateX(0)"},
                    "25%": {opacity: 1},
                    "100%": {opacity: 0, transform: "translateX(-26px)"},
                },
                "& .truck-svg": {
                    position: "absolute",
                    left: "50%",
                    bottom: 10,
                    transform: "translateX(-50%)",
                },
                "& .truck-body": {
                    animation: `truckBob ${isSolid ? "0.55s" : "0.7s"} ease-in-out infinite`,
                },
                "& .truck-wheel": {
                    animation: `truckWheelSpin ${isSolid ? "0.7s" : "0.9s"} linear infinite`,
                    transformBox: "fill-box",
                    transformOrigin: "center",
                },
                "& .truck-road": {
                    position: "absolute",
                    left: 16,
                    right: 16,
                    bottom: isSolid ? 13 : 14,
                    height: 2,
                    borderRadius: 999,
                    backgroundImage: `repeating-linear-gradient(to right, ${alpha(theme.palette.primary.main, isSolid ? 0.35 : 0.25)} 0, ${alpha(theme.palette.primary.main, isSolid ? 0.35 : 0.25)} 10px, transparent 10px, transparent 24px)`,
                    animation: `truckRoadMove ${isSolid ? "0.45s" : "0.55s"} linear infinite`,
                },
                "& .truck-speed-line": {
                    position: "absolute",
                    height: isSolid ? 2.5 : 2,
                    borderRadius: 999,
                    backgroundColor: alpha(theme.palette.primary.main, isSolid ? 0.45 : 0.3),
                    animation: `truckSpeedLine ${isSolid ? "0.9s" : "1s"} ease-out infinite`,
                },
                "@media (prefers-reduced-motion: reduce)": {
                    "& .truck-body, & .truck-wheel, & .truck-road, & .truck-speed-line": {animation: "none"},
                },
            }}
        >
            <Box className="truck-speed-line" sx={{left: 34, bottom: 48, width: 16, animationDelay: "0s"}} />
            <Box className="truck-speed-line" sx={{left: 28, bottom: 38, width: 22, animationDelay: "0.35s"}} />
            {isSolid ? <Box className="truck-speed-line" sx={{left: 36, bottom: 28, width: 14, animationDelay: "0.6s"}} /> : null}
            <Box className="truck-road" />
            {isSolid ? <SolidTruckSvg theme={theme} /> : <LineTruckSvg theme={theme} />}
        </Box>
    );
};
